import { memo, useEffect, useMemo, useState } from "react"
import { format } from "date-fns"
import { IconArrowsLeftRightTrailOutline18 as SuggestionsIcon } from "nucleo-ui-outline-18"

import { computeReviewMerge } from "@productlane/lib/src/prosemirror-diff"
import { Badge, Dialog, Empty, NucleoFillIcon } from "@productlane/ui"

import type { ClosedProposal } from "./suggestion-history"
import { VersionAuthorLine } from "@/components/version-author"
import { ReviewDiff } from "./review-diff"
import {
	baseHtmlOf,
	outcomeLabel,
	sortByClosedAt,
	stepsOf,
} from "./suggestion-history"

const NO_DISCARDED: ReadonlySet<string> = new Set()

interface Props {
	isOpen: boolean
	onOpenChange: (open: boolean) => void
	proposals: readonly ClosedProposal[]
	articleTitle: string
	articleIcon: string
	isLoading?: boolean
}

export const SuggestionHistoryDialog = memo(
	({
		isOpen,
		onOpenChange,
		proposals,
		articleTitle,
		articleIcon,
		isLoading,
	}: Props) => {
		const ordered = useMemo(() => sortByClosedAt(proposals), [proposals])
		const newest = ordered[0]
		const [selectedId, setSelectedId] = useState<string | undefined>(newest?.id)

		useEffect(() => {
			if (selectedId && ordered.some((p) => p.id === selectedId)) return
			setSelectedId(newest?.id)
		}, [ordered, selectedId, newest?.id])

		const selected = ordered.find((p) => p.id === selectedId) ?? newest

		return (
			<Dialog
				size="xl"
				isOpen={isOpen}
				onOpenChange={onOpenChange}
				title={
					<span className="flex items-center gap-1.5">
						<span className="font-normal text-secondary">
							Reviewed suggestions for
						</span>
						<span className="flex min-w-0 items-center gap-1.5">
							<NucleoFillIcon icon={articleIcon} className="h-4 w-4 shrink-0" />
							<span className="truncate">{articleTitle || "Document"}</span>
						</span>
					</span>
				}
				scrollContainerClasses="overflow-hidden p-0"
			>
				{ordered.length === 0 && isLoading ? null : ordered.length === 0 ? (
					<Empty
						image={<SuggestionsIcon className="h-8 w-8 text-gray-400" />}
						title="No reviewed suggestions"
						text="Suggestions you accept or reject stay here, so you can read them back later."
						className="h-[60dvh]"
					/>
				) : (
					<div className="flex h-[70dvh] min-h-0 overflow-hidden rounded-lg ring-1 ring-black/5 dark:ring-white/10">
						<aside className="w-[260px] shrink-0 overflow-y-auto border-r border-gray-200 px-2 pb-2 pt-3 dark:border-gray-700/80">
							{ordered.map((proposal) => (
								<ProposalRow
									key={proposal.id}
									proposal={proposal}
									isSelected={proposal.id === selected?.id}
									onSelect={() => setSelectedId(proposal.id)}
								/>
							))}
						</aside>

						<div className="relative min-w-0 flex-1 overflow-y-auto">
							{selected && (
								<ProposalPreview
									key={selected.id}
									proposal={selected}
									articleTitle={articleTitle}
									articleIcon={articleIcon}
								/>
							)}
						</div>
					</div>
				)}
			</Dialog>
		)
	},
)

SuggestionHistoryDialog.displayName = "SuggestionHistoryDialog"

function ProposalRow({
	proposal,
	isSelected,
	onSelect,
}: {
	proposal: ClosedProposal
	isSelected: boolean
	onSelect: () => void
}) {
	const outcome = outcomeLabel(proposal.status)
	const decidedAt = proposal.closedAt ?? proposal.createdAt
	return (
		<button
			type="button"
			onClick={onSelect}
			className={
				isSelected
					? "flex w-full flex-col gap-1 rounded-md bg-gray-150 px-2 py-1.5 text-left dark:bg-gray-800"
					: "flex w-full flex-col gap-1 rounded-md px-2 py-1.5 text-left hover:bg-gray-100 dark:hover:bg-gray-850"
			}
		>
			<span className="flex items-center justify-between gap-2">
				<span className="text-sm font-medium">
					{format(new Date(decidedAt), "MMM d, yyyy")}{" "}
					<span className="font-normal text-secondary">
						{format(new Date(decidedAt), "HH:mm")}
					</span>
				</span>
				<Badge variant={outcome.variant}>{outcome.label}</Badge>
			</span>
			<VersionAuthorLine
				source={proposal.authorType === "AGENT" ? "AI" : "USER"}
				user={proposal.createdBy}
			/>
		</button>
	)
}

function ProposalPreview({
	proposal,
	articleTitle,
	articleIcon,
}: {
	proposal: ClosedProposal
	articleTitle: string
	articleIcon: string
}) {
	const baseHtml = baseHtmlOf(proposal)
	const stepsJson = stepsOf(proposal)
	const merge = useMemo(
		() =>
			computeReviewMerge(
				baseHtml,
				[{ id: proposal.id, baseHtml, stepsJson }],
				NO_DISCARDED,
			),
		[baseHtml, proposal.id, stepsJson],
	)

	const outcome = outcomeLabel(proposal.status)
	const title = proposal.proposedTitle ?? articleTitle

	return (
		<div className="flex min-h-full flex-col">
			<div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-200 px-5 py-3 dark:border-gray-700/80">
				<Badge variant={outcome.variant}>{outcome.label}</Badge>
				{proposal.closedBy && (
					<span className="flex items-center gap-1.5 text-xs text-secondary">
						by
						<VersionAuthorLine source="USER" user={proposal.closedBy} />
					</span>
				)}
				{proposal.closedAt && (
					<span className="text-xs text-tertiary">
						{format(new Date(proposal.closedAt), "MMM d, yyyy 'at' HH:mm")}
					</span>
				)}
			</div>

			{proposal.group?.description && (
				<p className="border-b border-gray-200 px-5 py-3 text-sm text-secondary dark:border-gray-700/80">
					{proposal.group.description}
				</p>
			)}

			<div className="min-h-0 flex-1 px-1 pb-6">
				<ReviewDiff
					layout="flat"
					icon={articleIcon}
					articleTitle={title}
					baseHtml={baseHtml}
					mergedHtml={merge.resultHtml}
					hunkRanges={merge.hunkRanges}
					titleChange={
						proposal.proposedTitle && proposal.proposedTitle !== articleTitle
							? { from: articleTitle, to: proposal.proposedTitle }
							: null
					}
					fallback={
						<p className="px-5 py-6 text-sm text-secondary">
							The document version this suggestion was written against is no
							longer stored, so the change cannot be shown side by side.
						</p>
					}
				/>
			</div>
		</div>
	)
}
